/**
 * Her phone doing things on its own, on screen: text banners sliding down from
 * the top (her lock screen), and the video call / map glitch that end on his face.
 */

import { makeSwipeable } from './swipe';

export type CallStage = 'none' | 'ringing' | 'connecting' | 'face' | 'ended';

function el<T extends HTMLElement = HTMLElement>(selector: string): T {
  const e = document.querySelector<T>(selector);
  if (!e) throw new Error(`Missing element ${selector}`);
  return e;
}

/** Call timeline (s): ringing until RING, static until CONNECT, his face until FACE_END, "Call ended" until DONE. */
const RING = 2.8;
const CONNECT = 3.15;
const FACE_END = 4.05;
const DONE = 5.2;
/** Map flash timeline (s): static, his face, static, gone. */
const FLASH_IN = 0.12;
const FLASH_OUT = 0.44;
const FLASH_DONE = 0.62;

export class PhoneFx {
  private readonly banner = el('#text-banner');
  private readonly call = el('#phone-call');
  private readonly video = el<HTMLCanvasElement>('#call-video');
  private readonly noise: HTMLCanvasElement;
  private face: HTMLCanvasElement | null = null;
  private bannerLeft = 0;
  private mode: 'none' | 'call' | 'flash' = 'none';
  private t = 0;
  private current: CallStage = 'none';
  /** What the last scare was ('call' or 'flash'). */
  lastMode: 'none' | 'call' | 'flash' = 'none';

  constructor() {
    // Flick the banner away (sideways or up), like a real one.
    makeSwipeable(
      this.banner,
      () => {
        this.banner.classList.remove('show');
        this.bannerLeft = 0;
      },
      true,
    );
    // A tile of static, drawn at random offsets each frame.
    this.noise = document.createElement('canvas');
    this.noise.width = 256;
    this.noise.height = 256;
    const ctx = this.noise.getContext('2d');
    if (ctx) {
      const img = ctx.createImageData(256, 256);
      for (let i = 0; i < img.data.length; i += 4) {
        const v = Math.random() * 255;
        img.data[i] = v;
        img.data[i + 1] = v;
        img.data[i + 2] = v;
        img.data[i + 3] = 255;
      }
      ctx.putImageData(img, 0, 0);
    }
  }

  /** His face, for the call and the flash. */
  setFace(face: HTMLCanvasElement | null): void {
    this.face = face;
  }

  /** The phone should be up on screen (a call is in progress). */
  get calling(): boolean {
    return this.mode === 'call';
  }

  get flashing(): boolean {
    return this.mode === 'flash';
  }

  get stage(): CallStage {
    return this.current;
  }

  get busy(): boolean {
    return this.mode !== 'none';
  }

  /** A notification on her lock screen. `image` is a photo attachment. */
  showBanner(from: string, text: string, tone: 'normal' | 'system' = 'normal', image?: string): void {
    const b = this.banner;
    b.classList.toggle('system', tone === 'system');
    b.classList.toggle('photo', !!image);
    el('#text-banner .tb-icon').textContent = tone === 'system' ? '!' : (from.trim()[0] ?? '?').toUpperCase();
    el('#text-banner .tb-from').textContent = from;
    el('#text-banner .tb-text').textContent = text;
    const img = el<HTMLImageElement>('#text-banner .tb-image');
    if (image) img.src = image;
    else img.removeAttribute('src');
    b.classList.remove('show');
    void b.offsetWidth;
    b.classList.add('show');
    this.bannerLeft = image ? 7.5 : 5;
  }

  startCall(): void {
    this.mode = 'call';
    this.lastMode = 'call';
    this.t = 0;
  }

  flash(): void {
    this.mode = 'flash';
    this.lastMode = 'flash';
    this.t = 0;
  }

  /** Stop everything at once (restart, caught, new night). */
  clear(): void {
    this.mode = 'none';
    this.setStage('none');
    this.banner.classList.remove('show');
    this.bannerLeft = 0;
  }

  /** Advance; returns the stage when it changes (so the game can play the sound for it). */
  update(dt: number): CallStage | null {
    if (this.bannerLeft > 0) {
      this.bannerLeft -= dt;
      if (this.bannerLeft <= 0) this.banner.classList.remove('show');
    }
    if (this.mode === 'none') return null;
    this.t += dt;
    let stage: CallStage;
    if (this.mode === 'call') {
      stage = this.t < RING ? 'ringing' : this.t < CONNECT ? 'connecting' : this.t < FACE_END ? 'face' : this.t < DONE ? 'ended' : 'none';
    } else {
      stage = this.t < FLASH_IN ? 'connecting' : this.t < FLASH_OUT ? 'face' : this.t < FLASH_DONE ? 'connecting' : 'none';
    }
    if (stage === 'none') this.mode = 'none';
    const changed = stage !== this.current;
    this.setStage(stage);
    this.draw();
    return changed ? stage : null;
  }

  private setStage(stage: CallStage): void {
    if (stage === this.current) return;
    this.current = stage;
    this.call.dataset.stage = stage;
    this.call.classList.toggle('flash', this.mode === 'flash');
  }

  private draw(): void {
    const c = this.video;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const W = c.width;
    const H = c.height;
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.filter = 'none';
    ctx.fillStyle = '#030405';
    ctx.fillRect(0, 0, W, H);
    const face = this.face;
    switch (this.current) {
      case 'ringing': {
        // The caller's camera preview: almost nothing. A shape, close to the lens.
        if (face) {
          ctx.filter = 'blur(7px) brightness(0.22) contrast(1.2)';
          this.drawCover(ctx, face, 1.15, 0, 0);
          ctx.filter = 'none';
        }
        this.drawNoise(ctx, 0.1);
        break;
      }
      case 'connecting':
        this.drawNoise(ctx, this.mode === 'flash' ? 0.55 : 0.85);
        break;
      case 'face': {
        const k = this.mode === 'call' ? Math.min(1, (this.t - CONNECT) / (FACE_END - CONNECT)) : (this.t - FLASH_IN) / (FLASH_OUT - FLASH_IN);
        if (face) {
          // Lunging closer, shaking, overexposed by his own flash.
          const jx = (Math.random() - 0.5) * 14;
          const jy = (Math.random() - 0.5) * 10;
          ctx.filter = k < 0.08 ? 'brightness(2.6) contrast(1.4) grayscale(0.6)' : 'grayscale(0.65) contrast(1.7) brightness(1.05)';
          this.drawCover(ctx, face, 1 + k * 0.3, jx, jy);
          ctx.filter = 'none';
          // Torn scanlines.
          for (let i = 0; i < 4; i += 1) {
            if (Math.random() < 0.45) continue;
            const y = Math.random() * H;
            const h = 4 + Math.random() * 22;
            ctx.drawImage(c, 0, y, W, h, (Math.random() - 0.5) * 40, y, W, h);
          }
          // A cold, cheap front camera, dark at the edges.
          ctx.globalCompositeOperation = 'multiply';
          ctx.fillStyle = '#b9c8d8';
          ctx.fillRect(0, 0, W, H);
          ctx.globalCompositeOperation = 'source-over';
          const v = ctx.createRadialGradient(W / 2, H * 0.4, W * 0.25, W / 2, H * 0.45, H * 0.62);
          v.addColorStop(0, 'rgba(0,0,0,0)');
          v.addColorStop(1, 'rgba(0,0,0,0.85)');
          ctx.fillStyle = v;
          ctx.fillRect(0, 0, W, H);
        }
        this.drawNoise(ctx, 0.16);
        break;
      }
      case 'ended':
      default:
        break;
    }
    ctx.restore();
  }

  private drawCover(ctx: CanvasRenderingContext2D, img: HTMLCanvasElement, zoom: number, dx: number, dy: number): void {
    const W = ctx.canvas.width;
    const H = ctx.canvas.height;
    const s = Math.max(W / img.width, H / img.height) * zoom;
    const w = img.width * s;
    const h = img.height * s;
    ctx.drawImage(img, (W - w) / 2 + dx, (H - h) / 2 + dy, w, h);
  }

  private drawNoise(ctx: CanvasRenderingContext2D, alpha: number): void {
    const W = ctx.canvas.width;
    const H = ctx.canvas.height;
    ctx.globalAlpha = alpha;
    const ox = Math.random() * 256;
    const oy = Math.random() * 256;
    for (let y = -oy; y < H; y += 256) {
      for (let x = -ox; x < W; x += 256) ctx.drawImage(this.noise, x, y);
    }
    ctx.globalAlpha = 1;
  }
}
